import { useEffect, useState } from "react"
import { Alert, Form, Input } from "antd"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { AxiosError } from "axios"
import { useAuth } from "../../components/context/AuthContext"
import AuthPageLayout, { PrimaryButton } from "../Login/AuthPageLayout"
import PasswordRequirementsList, { passwordConfirmFieldRules, passwordFieldRules } from "./PasswordRequirementsList"
import { postPasswordReset } from "./apiCalls"
import { readTokenFromHash, removeHashFromUrl } from "./readToken"

type TPasswordSetForm = {
    password: string
    confirmPassword: string
}

type TResetErrorBody = { message?: string; displayMessage?: string; details?: { path?: string; message?: string }[] }

type TResetError = { text: string; invalidLink: boolean; details?: string[] }

const INVALID_LINK_TEXT = "Der Link ist ungültig oder abgelaufen."

/**
 * 400 ist entweder ein ungültiger/abgelaufener Token ("Invalid or expired token") oder ein Validierungsfehler des Bodys ("Validation failed" mit `details`).
 * Nur ein Token-Fehler (auch Validierungsfehler am Feld `token`) zeigt „Link ungültig“; Passwort-Fehler zeigen die Meldungen des Backends.
 */
const resetError = (error: unknown): TResetError => {
    const response = (error as AxiosError<TResetErrorBody>)?.response
    const data = response?.data
    if (response?.status === 400 && data?.message === "Validation failed" && Array.isArray(data.details)) {
        if (data.details.some((detail) => detail.path === "token")) return { text: INVALID_LINK_TEXT, invalidLink: true }
        const details = data.details.map((detail) => detail.message).filter((message): message is string => !!message)
        return { text: data.displayMessage ?? "Ungültige Eingabedaten", invalidLink: false, details }
    }
    if (response?.status === 400 || response?.status === 401) return { text: INVALID_LINK_TEXT, invalidLink: true }
    if (data?.displayMessage) return { text: data.displayMessage, invalidLink: false }
    if (response?.status === 429) return { text: "Zu viele Versuche. Bitte warten Sie einen Moment und versuchen Sie es erneut.", invalidLink: false }
    return { text: "Das Passwort konnte nicht gesetzt werden. Bitte versuchen Sie es später erneut.", invalidLink: false }
}

/**
 * `/passwort/neu#t=<token>` (Einladung und „Passwort vergessen“).
 * Der Token wird einmal aus dem Fragment gelesen und sofort aus Adresszeile und Router-Location entfernt; er steht danach nur im State und im Body des Reset-Requests.
 */
export default function PasswordSet() {
    const auth = useAuth()
    const location = useLocation()
    const navigate = useNavigate()
    // reines Lesen im Initializer; das Entfernen passiert im Effect direkt nach dem ersten Rendern (vor jedem Request mit Token)
    const [token] = useState(() => readTokenFromHash(location.hash || window.location.hash))
    const [form] = Form.useForm<TPasswordSetForm>()
    const password = Form.useWatch("password", form) ?? ""
    const confirmPassword = Form.useWatch("confirmPassword", form) ?? ""
    const [submitting, setSubmitting] = useState(false)
    const [showRequirementErrors, setShowRequirementErrors] = useState(false)
    const [error, setError] = useState<TResetError | undefined>(undefined)
    const [done, setDone] = useState(false)

    useEffect(() => {
        removeHashFromUrl()
        // auch die Router-Location ohne Fragment ersetzen, damit der Token nicht im Router-State weiterlebt
        if (location.hash) navigate(location.pathname + location.search, { replace: true, state: location.state })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onFinish = async (values: TPasswordSetForm) => {
        if (!token) return
        setSubmitting(true)
        setError(undefined)
        try {
            await postPasswordReset(token, values.password)
            removeHashFromUrl()
            setDone(true)
        } catch (caught) {
            setError(resetError(caught))
        } finally {
            setSubmitting(false)
        }
    }

    if (done)
        return (
            <AuthPageLayout title="Passwort gesetzt">
                <Alert type="success" showIcon message="Ihr Passwort wurde gesetzt. Sie können sich jetzt anmelden." />
                {/* ist in diesem Browser noch jemand am Portal angemeldet: erst abmelden, das führt zur Anmeldung */}
                <PrimaryButton type="button" onClick={() => (auth.isAuthenticated ? void auth.logout() : navigate("/login"))}>
                    Zur Anmeldung
                </PrimaryButton>
            </AuthPageLayout>
        )

    if (!token)
        return (
            <AuthPageLayout title="Passwort setzen">
                <Alert type="error" showIcon message={INVALID_LINK_TEXT} />
                <RequestNewLink />
            </AuthPageLayout>
        )

    return (
        <AuthPageLayout title="Passwort setzen">
            {error && (
                <Alert
                    type="error"
                    showIcon
                    message={error.text}
                    description={
                        error.details?.length ? (
                            <ul className="list-disc pl-4">
                                {error.details.map((detail) => (
                                    <li key={detail}>{detail}</li>
                                ))}
                            </ul>
                        ) : undefined
                    }
                />
            )}
            {error?.invalidLink && <RequestNewLink />}
            <Form<TPasswordSetForm>
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={onFinish}
                onFinishFailed={() => setShowRequirementErrors(true)}
                disabled={submitting}>
                <Form.Item label="Neues Passwort" name="password" rules={passwordFieldRules}>
                    <Input.Password autoComplete="new-password" autoFocus size="large" />
                </Form.Item>
                <Form.Item
                    label="Passwort wiederholen"
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={passwordConfirmFieldRules("password")}>
                    <Input.Password autoComplete="new-password" size="large" />
                </Form.Item>
                <PasswordRequirementsList password={password} confirmPassword={confirmPassword} emphasizeUnmet={showRequirementErrors} />
                <PrimaryButton type="submit" disabled={submitting} loading={submitting}>
                    Passwort speichern
                </PrimaryButton>
            </Form>
        </AuthPageLayout>
    )
}

function RequestNewLink() {
    return (
        <div className="text-center">
            <Link to="/passwort/vergessen" className="text-primary hover:underline">
                Neuen Link anfordern
            </Link>
        </div>
    )
}
